/**
 * Bridge between the chat controller and the session-uploads store.
 *
 * Lives here rather than in `controllers/session-uploads.ts` because that
 * module is intentionally host-agnostic (pure state transitions, no client
 * access), while this one holds the client handle.
 */

import type { ChatState } from "./chat.ts";
import { listSessionFiles } from "../chat/session-files.ts";
import { emptySessionUploadsState } from "./session-uploads.ts";

/**
 * Load every file stored for a session into `state.sessionUploads`.
 *
 * Called on a session (re)load so the Files tab shows uploads from earlier
 * turns after a reload. Never throws: a failed list leaves the previously
 * known files in place rather than blanking the section.
 */
export async function loadSessionUploadsIntoState(
  state: ChatState,
  sessionKey: string,
): Promise<void> {
  const key = (sessionKey ?? "").trim();
  if (!key) {
    state.sessionUploads = emptySessionUploadsState();
    return;
  }
  // Don't clobber a list the operator is already looking at for THIS session
  // if a second load races the first.
  if (state.sessionUploads?.sessionKey !== key) {
    state.sessionUploads = { ...emptySessionUploadsState(), sessionKey: key, loading: true };
  }
  try {
    const files = await listSessionFiles({
      origin: state.client?.getHttpOrigin?.() ?? "",
      token: state.client?.token?.(),
      sessionKey: key,
    });
    // The operator may have switched sessions while we were in flight.
    if (state.sessionKey.trim() !== key) {
      return;
    }
    state.sessionUploads = { sessionKey: key, files, loading: false, error: null, loaded: true };
  } catch (err) {
    if (state.sessionKey.trim() !== key) {
      return;
    }
    state.sessionUploads = {
      ...(state.sessionUploads ?? emptySessionUploadsState()),
      sessionKey: key,
      loading: false,
      loaded: true,
      error: err instanceof Error ? err.message : "could not load session uploads",
    };
  }
}